import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { MUNICIPIOS_IDS } from '../../constants/municipios'
import { ComponentRenderer } from '../../components/ComponentRenderer'

interface CategoryTab {
  id: number
  name: string
  id_especie: number
  show_species_data: boolean
  orderlevel: number
}

interface DashboardComponent {
  id: string
  position: number
  grid_column: number
  grid_row: number
  grid_x: number
  grid_y: number
  components: {
    id: string
    name: string
    type: string
    configuration: any
  }
}

interface Dashboard {
  id: string
  name: string
  slug: string
  description: string | null
  dashboard_components: DashboardComponent[]
}

interface Municipio {
  id_municipio: string
  nombre: string
}

export default function DatosForestales() {
  const [tabs, setTabs] = useState<CategoryTab[]>([])
  const [activeTab, setActiveTab] = useState<CategoryTab | null>(null)
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [municipios, setMunicipios] = useState<Municipio[]>([])
  const [loadingTabs, setLoadingTabs] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMunicipio, setSelectedMunicipio] = useState('')
  const [selectedEspecie, setSelectedEspecie] = useState<number | 0>(0)

  // Cargar las categorías con show_species_data = true
  useEffect(() => {
    loadSpeciesCategories()
    loadMunicipios()
  }, [])

  // Cargar dashboard cuando cambia la pestaña activa
  useEffect(() => {
    if (activeTab) {
      setSelectedEspecie(activeTab.id_especie)
      loadDashboard(activeTab.id)
    }
  }, [activeTab])

  const loadSpeciesCategories = async () => {
    try {
      setLoadingTabs(true)
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, id_especie, show_species_data, orderlevel')
        .eq('show_species_data', true)
        .is('parent_id', null) // Solo categorías padre
        .order('orderlevel', { ascending: true })

      if (error) throw error

      const validTabs = (data || []).filter((cat:any) => cat.id_especie !== null)
      
      setTabs(validTabs)
      
      // Establecer la primera pestaña como activa si hay datos
      if (validTabs.length > 0) {
        setActiveTab(validTabs[0])
      }
    } catch (err) {
      console.error('Error cargando categorías de especies:', err)
      setTabs([])
    } finally {
      setLoadingTabs(false)
    }
  }

  const loadMunicipios = async () => {
    try {
      const { data, error } = await supabase
        .from('municipios')
        .select('id_municipio, nombre')
        .in('id_municipio', MUNICIPIOS_IDS)
        .order('nombre', { ascending: true })

      if (error) throw error
      setMunicipios(data || [])
    } catch (err) {
      console.error('Error cargando municipios', err)
      setMunicipios([])
    } finally {
    }
  }

  const loadDashboard = async (categoryId: number) => {
    try {
      setLoading(true)
      setError(null)

      // Buscar el dashboard por category_id en lugar de slug
      const { data, error: fetchError } = await supabase
        .from('dashboards')
        .select(`
          id,
          name,
          slug,
          description,
          dashboard_components (
            id,
            position,
            grid_column,
            grid_row,
            grid_x,
            grid_y,
            components (
              id,
              name,
              type,
              configuration
            )
          )
        `)
        .eq('category_id', categoryId)
        .limit(1)
        .single()

      if (fetchError) {
        // Si no hay dashboard para esta categoría, establecer como null
        if (fetchError.code === 'PGRST116') {
          setDashboard(null)
          return
        }
        throw fetchError
      }
      
      setSelectedMunicipio('')
      setDashboard(data)
    } catch (err) {
      console.error(err)
      setError('error-loading')
      setDashboard(null)
    } finally {
      setLoading(false)
    }
  }

  if (loadingTabs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-bmp-purple mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando especies...</p>
        </div>
      </div>
    )
  }

  if (tabs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🌲</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay especies disponibles</h3>
          <p className="text-gray-500">No se han configurado especies para mostrar.</p>
        </div>
      </div>
    )
  }

  if (loading && activeTab) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-bmp-purple mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando dashboard...</p>
        </div>
      </div>
    )
  }

  const sortedComponents = dashboard 
    ? [...(dashboard.dashboard_components || [])].sort((a, b) => a.position - b.position)
    : []

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className='bg-card border rounded-lg p-6'>
        <div className="border rounded-lg p-4 transition-all">
          {/* Pestañas de especies */}
          <div className="flex gap-2 overflow-x-auto pb-3">
            {tabs.map((tab) => {
              const active = activeTab?.id === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all
                    border
                    ${
                      active
                        ? 'bg-green-600 text-white border-green-600 shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                    }
                  `}
                >
                  {tab.name}
                </button>
              )
            })}
          </div>
          <br />

          {/* Selector de municipio (opcional, descomenta si lo necesitas) */}
          <div className="w-full max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filtra por municipio
            </label>
            <div className="relative">
              <select
                value={selectedMunicipio}
                onChange={(e) => setSelectedMunicipio(e.target.value)}
                className="
                  w-full appearance-none rounded-lg border border-gray-300 bg-white
                  px-3 py-2 pr-10 text-sm
                  focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              >
                <option value="">Todos los municipios</option>
                {municipios?.map((m: any) => (
                  <option key={m.id_municipio} value={m.id_municipio}>
                    {m.nombre}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                ▼
              </span>
            </div>
            <br />
          </div> 

          {/* Contenido del dashboard */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            {activeTab && (
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {activeTab.name}
                </h2>
              </div>
            )}

            {error && (
              <div className="text-center py-10">
                <div className="text-5xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Error al cargar el dashboard
                </h3>
                <p className="text-gray-500">No se pudo cargar la información de esta especie.</p>
              </div>
            )}

            {!error && !dashboard && activeTab && (
              <div className="text-center py-10">
                <div className="text-5xl mb-4">📊</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No hay dashboard disponible
                </h3>
                <p className="text-gray-500">
                  Esta especie no tiene un dashboard configurado.
                </p>
              </div>
            )}

            {!error && dashboard && (
              <main className="container mx-auto px-4 py-8 flex-1">
                {dashboard.description && (
                  <p className="text-gray-600 mb-6">{dashboard.description}</p>
                )}

                <div className="mx-auto" style={{ maxWidth: '1400px' }}>
                  {sortedComponents.length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        Dashboard vacío
                      </h3>
                      <p className="text-gray-500">
                        Este dashboard no tiene componentes configurados.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop */}
                      <div
                        className="hidden lg:grid gap-4"
                        style={{
                          gridTemplateColumns: 'repeat(12, 1fr)',
                          gridAutoRows: '120px',
                        }}
                      >
                        {sortedComponents.map((dashComp) => {
                          const c = dashComp.components
                          return (
                            <div
                              key={dashComp.id}
                              style={{
                                gridColumn: `${dashComp.grid_x + 1} / span ${dashComp.grid_column}`,
                                gridRow: `${dashComp.grid_y + 1} / span ${dashComp.grid_row}`,
                              }}
                            >
                              <ComponentRenderer
                                component={{
                                  id: c.id,
                                  name: c.name,
                                  type: c.type as any,
                                  configuration: {
                                    ...c.configuration, // Mantener la configuración original
                                    filterMunicipio: selectedMunicipio || null,
                                    filterEspecie: selectedEspecie || null
                                  },
                                }}
                                filterMunicipio={selectedMunicipio}
                                filterEspecie={selectedEspecie}
                                filterMunicipioNombre={selectedMunicipio ? 
                                    municipios.find(m => m.id_municipio == selectedMunicipio)?.nombre || null 
                                    : null}
                              />
                            </div>
                          )
                        })}
                      </div>

                      {/* Mobile */}
                      <div className="flex flex-col gap-6 lg:hidden">
                        {sortedComponents.map((dashComp) => {
                          const c = dashComp.components
                          return (
                            <ComponentRenderer
                              key={dashComp.id}
                              component={{
                                id: c.id,
                                name: c.name,
                                type: c.type as any,
                                configuration: {
                                    ...c.configuration, // Mantener la configuración original
                                    filterMunicipio: selectedMunicipio || null,
                                    filterEspecie: selectedEspecie || null
                                  },
                              }}
                              filterMunicipio={selectedMunicipio}
                              filterEspecie={selectedEspecie}
                            />
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              </main>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}